import type { Dispatch, SetStateAction } from 'react';
import { ChatAttachmentChipComponent } from './ChatAttachmentChipComponent.js';
import type { PendingAttachment } from './ChatAttachmentPickerComponent.js';
import './ChatAttachmentTrayComponent.css';

interface ChatAttachmentTrayProps {
  attachments: PendingAttachment[];
  onAttachmentsChange: Dispatch<SetStateAction<PendingAttachment[]>>;
}

/**
 * The files staged for the next message, on their own full-width row above the
 * composer.
 *
 * Split out of the picker so the chips stop sharing the composer's narrow action
 * column. Rendered there, a single attachment took ~114px off the textarea — on a
 * 390px screen that left a typing box narrower than the chip beside it, with the
 * placeholder wrapping onto two lines. Width the chips need is width the message
 * does not have.
 */
export function ChatAttachmentTrayComponent({ attachments, onAttachmentsChange }: ChatAttachmentTrayProps): JSX.Element | null {
  if (attachments.length === 0) return null;

  function handleRemove(localId: string): void {
    onAttachmentsChange((prev) => prev.filter((a) => a.localId !== localId));
  }

  return (
    <div className="chat-attachment-tray" aria-label="Files attached to this message">
      {attachments.map((a) => (
        <ChatAttachmentChipComponent
          key={a.localId}
          fileName={a.fileName}
          mimeType={a.mimeType}
          size={a.size}
          status={a.status}
          errorMessage={a.errorMessage}
          onRemove={() => handleRemove(a.localId)}
        />
      ))}
    </div>
  );
}
